import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

import LoadingSpinner from "../../common/loaders/loadingSpinner";
import { BreadCrumbs } from "../../common/layout";
import useApi from "../../../hooks/useApi";

import entityService from "../../../services/entityService";
import authService from "../../../services/authService";

import SettingsContact from "../../entities/_settingsContact";

import TeamMembersHeader from "./TeamMembersHeader";
import TeamMembersTable from "./TeamMembersTable";
import BulkNewsletterModal from "./BulkNewsletterModal";
import BulkManagingContactModal from "./BulkManagingContactModal";
import AdditionalFiltersModal from "./AdditionalFiltersModal";
import TopWidgets from "./TopWidgets";
import EditEntityTeamMember from "./EditEntityTeamMember";
import NewTrader from "../network/newTrader";

import "../../../styles/react-table.css";
import "react-table/react-table.css";

function AllEntityTeamMembers() {
  const tableRef = useRef(null);

  const { entity_id, category, constants, country_id } =
    authService.getCurrentUser();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedContact, setSelectedContact] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const [updateModal, setUpdateModal] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [newsletterModal, setNewsletterModal] = useState(false);
  const [managingContactModal, setManagingContactModal] = useState(false);

  const [newsletter, setNewsletter] = useState("yes");
  const [managingContact, setManagingContact] = useState(null);
  const [managingContactList, setManagingContactList] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [owner, setOwner] = useState(null);
  const [country, setCountry] = useState(null);
  const [categoryList, setCategoryList] = useState([]);

  const [additionalFiltersModal, setAdditionalFiltersModal] = useState(false);
  const [additionalFilter, setAdditionalFilter] = useState({
    managedBy: null,
    newsletter: null,
    status: null,
    thisMonth: null,
    category: null,
  });

  const [teamMembers, setTeamMembers] = useState([]);
  const [secondaryAccountModal, setSecondaryAccountModal] = useState(false);

  const [widgetData, setWidgetData] = useState({
    totalEntities: 0,
    newEntitiesThisMonth: 0,
    newEntitiesPercent: 0,
    marketableEntities: 0,
    marketableEntitiesPercent: 0,
  });

  const fetchAllTeamMembersApi = useApi(entityService.fetchAllTeamMembers);
  const bulkUpdateNewsletterApi = useApi(entityService.bulkUpdateNewsletter);
  const topWidgetsApi = useApi(entityService.entitiesConsolidation);
  const bulkUpdateManagingContactApi = useApi(
    entityService.bulkUpdateManagingContact
  );
  const bulkSyncToHubspotApi = useApi(entityService.bulkSyncToHubspot);
  const syncToHubspotApi = useApi(entityService.syncToHubspot);
  const deleteEntityContactApi = useApi(entityService.deleteEntityContact);
  const loadParentContactsApi = useApi(entityService.getEntityContactsList);
  const getEntityCategoryListApi = useApi(entityService.getEntityCategoryList);

  const checkedRows = selectedRows.filter((obj) => obj.checked);

  const reloadTable = () => {
    if (tableRef.current) {
      tableRef.current.reloadData();
    }
  };

  const clearSelectionAndReload = () => {
    setSelectedRows([]);

    if (tableRef.current?.clearSelection) {
      tableRef.current.clearSelection();
    }

    reloadTable();
  };

  const loadWidgets = async () => {
    try {
      const response = await topWidgetsApi.request({
        searchKeyword,
        country:
          category === constants.CATEGORY.COUNTRY_OFFICE ? country_id : country,
        bp: owner === "all" ? null : owner,
        ...additionalFilter,
      });

      if (response?.data?.success) {
        setWidgetData(response.data.rows || {});
      }
    } catch (error) {
      toast.error("Failed to load widgets");
    }
  };

  const loadCategoryList = async () => {
    try {
      const response = await getEntityCategoryListApi.request();

      if (response?.data?.success) {
        setCategoryList(response.data.rows || []);
      }
    } catch (error) {
      setCategoryList([]);
    }
  };

  const fetchParentTeamMembers = async () => {
    try {
      const response = await loadParentContactsApi.request(entity_id);
      const rows = response?.data?.rows || [];

      const parentEntityContacts = rows.map((item) => ({
        label: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
        value: item.contact_id,
      }));

      setTeamMembers(parentEntityContacts);
      setManagingContactList(parentEntityContacts);
    } catch (error) {
      setTeamMembers([]);
      setManagingContactList([]);
    }
  };

  const handleCustomSearch = (e) => {
    const keyword = e.target.value;
    setSearchKeyword(keyword.replace(/^ /, ""));
  };

  const handleKeyPress = (e) => {
    if (
      e.key === "Enter" &&
      e.target.value.length > 0 &&
      e.target.value.length < 3
    ) {
      toast.error("Enter atleast 3 characters");
    }
  };

  const clearSearch = () => {
    if (searchKeyword.length > 0) {
      setSearchKeyword("");
      return;
    }

    reloadTable();
  };

  const handleEdit = (item) => {
    setSelectedContact(item);
    setUpdateModal(true);
  };

  const handleSettings = (item) => {
    setSelectedContact(item);
    setSettingsModal(true);
  };

  const handleDelete = (contactId) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="custom-delete-ui">
          <h4>Confirm to delete</h4>
          <p>Are you sure to delete this contact?</p>

          <button
            className="btn btn-primary mr-2"
            onClick={async () => {
              onClose();

              const response = await deleteEntityContactApi.request({
                contact_id: contactId,
              });

              if (response?.success || response?.data?.success) {
                toast.success(
                  response?.message ||
                    response?.data?.message ||
                    "Contact deleted successfully"
                );

                reloadTable();
                loadWidgets();
              } else {
                toast.error(
                  response?.message ||
                    response?.data?.message ||
                    "Failed to delete contact"
                );
              }
            }}
          >
            Yes
          </button>

          <button className="btn btn-white" onClick={onClose}>
            No
          </button>
        </div>
      ),
    });
  };

  const handleExportToExcel = async () => {
    try {
      const response = await fetchAllTeamMembersApi.request({
        searchKeyword,
        download: true,
        country:
          category === constants.CATEGORY.COUNTRY_OFFICE ? country_id : country,
        bp: owner === "all" ? null : owner,
        ...additionalFilter,
      });

      if (response?.data) {
        window.open(response.data, "_blank", "noreferrer");
      } else {
        toast.error("Failed to export entity team members");
      }
    } catch (error) {
      toast.error("Failed to export entity team members");
    }
  };

  const handleNewsletterUpdate = async () => {
    if (!checkedRows.length) {
      toast.error("Please select at least one contact");
      return;
    }

    try {
      setIsLoading(true);

      const response = await bulkUpdateNewsletterApi.request({
        newsletter,
        selectedAttributes: checkedRows,
      });

      if (response?.data?.success) {
        toast.success(response.data.message);
        setNewsletterModal(false);
        setNewsletter("yes");
        clearSelectionAndReload();
        loadWidgets();
      } else {
        toast.error(response?.data?.message || "Failed to update newsletter");
      }
    } catch (error) {
      toast.error("Failed to update newsletter");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManagingContactUpdate = async () => {
    if (!checkedRows.length) {
      toast.error("Please select at least one contact");
      return;
    }

    if (!managingContact) {
      toast.error("Please select a managing contact");
      return;
    }

    try {
      setIsLoading(true);

      const response = await bulkUpdateManagingContactApi.request({
        managing_contact: managingContact,
        selectedAttributes: checkedRows,
      });

      if (response?.data?.success) {
        toast.success(response.data.message);
        setManagingContactModal(false);
        setManagingContact(null);
        clearSelectionAndReload();
        loadWidgets();
      } else {
        toast.error(
          response?.data?.message || "Failed to update managing contact"
        );
      }
    } catch (error) {
      toast.error("Failed to update managing contact");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncToHubspot = async (row) => {
   
    try {
      setIsLoading(true);

      const res = await syncToHubspotApi.request({
        contact_id: row.contact_id,
      });
      console.log(res);
      if (res?.data?.success) {
        toast.success("Synced to HubSpot successfully");
        reloadTable();
        loadWidgets();
      } else {
        toast.error(res?.data?.message || "Sync failed");
      }
    } catch (error) {
      toast.error("Error syncing to HubSpot");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkSyncToHubspot = async () => {
    if (!checkedRows.length) {
      toast.error("Please select at least one contact");
      return;
    }

    try {
      setIsLoading(true);

      const contactIds = checkedRows.map((row) => row.contact_id);

      const response = await bulkSyncToHubspotApi.request({
        contact_ids: contactIds,
      });

      if (response?.data?.success) {
        toast.success(response.data.message);

        if (response.data.failed_count > 0) {
          toast.warning(
            `${response.data.failed_count} contact(s) failed to sync`
          );
        }

        clearSelectionAndReload();
        loadWidgets();
      } else {
        toast.error(response?.data?.message || "Bulk sync failed");
      }
    } catch (error) {
      toast.error("Failed to sync contacts to HubSpot");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOwner = (value) => {
    setOwner(value === null ? "all" : value);
  };

  const handleCountry = (value) => {
    setCountry(value);
    setOwner(null);
  };

  const handleUpdateFilter = (property, value) => {
    setAdditionalFilter((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  const handleApplyAdditionalFilter = () => {
    setAdditionalFiltersModal(false);
  };

  const handleResetAdditionalFilter = () => {
    setAdditionalFilter({
      managedBy: null,
      newsletter: null,
      status: null,
      thisMonth: null,
      category: null,
    });

    setAdditionalFiltersModal(false);
  };

  const isFirstSearchRender = useRef(true);
  const isFirstFilterRender = useRef(true);

  useEffect(() => {
    loadWidgets();
    fetchParentTeamMembers();
    loadCategoryList();
  }, [entity_id]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }

    const reloadSearchData = async () => {
      if (searchKeyword.length > 2 || searchKeyword.length === 0) {
        try {
          setIsLoading(true);
          reloadTable();
          await loadWidgets();
        } finally {
          setIsLoading(false);
        }
      }
    };

    reloadSearchData();
  }, [searchKeyword]);

  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }

    if (tableRef.current?.clearSelection) {
      tableRef.current.clearSelection();
    }

    setSelectedRows([]);
    reloadTable();
    loadWidgets();
  }, [owner, country, additionalFilter]);

  return (
    <>
      {isLoading && <LoadingSpinner />}

      <Helmet>
        <title>All Entities | {global.config.title}</title>
      </Helmet>

      <BreadCrumbs
        Header="Entities"
        activeModule="Entities"
        Route1="/dashboard"
        Label1="Dashboard"
      />

      <TopWidgets
        totalEntities={widgetData.totalEntities || 0}
        newEntitiesThisMonth={widgetData.newEntitiesThisMonth || 0}
        newEntitiesPercent={`${widgetData.newEntitiesPercent || 0}%`}
        marketableEntities={widgetData.marketableEntities || 0}
        marketableEntitiesPercent={`${widgetData.marketableEntitiesPercent || 0}%`}
        onClickTotal={() => {
          setAdditionalFilter({
            managedBy: null,
            newsletter: null,
            status: null,
            thisMonth: null,
          });
        }}
        onClickNewThisMonth={() => {
          setAdditionalFilter({
            managedBy: null,
            newsletter: null,
            status: null,
            thisMonth: "1",
          });
        }}
        onClickMarketable={() => {
          setAdditionalFilter((prev) => ({
            ...prev,
            managedBy: null,
            status: null,
            newsletter: "1",
            thisMonth: null,
          }));
        }}
      />

      <div className="card overflow-visible p-3">
        <TeamMembersHeader
          searchKeyword={searchKeyword}
          handleCustomSearch={handleCustomSearch}
          handleKeyPress={handleKeyPress}
          clearSearch={clearSearch}
          handleExportToExcel={handleExportToExcel}
          category={category}
          constants={constants}
          country={country}
          owner={owner}
          handleCountry={handleCountry}
          handleOwner={handleOwner}
          onOpenAdditionalFilters={() => setAdditionalFiltersModal(true)}
          onOpenSecondaryAccount={() => setSecondaryAccountModal(true)}
        />

        <div className="card-body">
          <TeamMembersTable
            tableRef={tableRef}
            service={entityService.fetchAllTeamMembers}
            searchKeyword={searchKeyword}
            owner={owner}
            country={country}
            additionalFilter={additionalFilter}
            category={category}
            countryId={country_id}
            onSelectRow={setSelectedRows}
            selectedRows={selectedRows}
            entityId={entity_id}
            constants={constants}
            onEdit={handleEdit}
            onSettings={handleSettings}
            onOpenNewsletterModal={() => setNewsletterModal(true)}
            onOpenManagingContactModal={() => setManagingContactModal(true)}
            onDelete={handleDelete}
            onSyncToHubspot={handleSyncToHubspot}
            onBulkSyncToHubspot={handleBulkSyncToHubspot}
          />
        </div>
      </div>

      <Modal show={updateModal} size="lg" onHide={() => setUpdateModal(false)}>
        <Modal.Header closeButton>
          <h6>Update Team Member</h6>
        </Modal.Header>

        <EditEntityTeamMember
          data={selectedContact}
          onCloseModal={() => setUpdateModal(false)}
          onHandleReload={() => {
            setUpdateModal(false);
            reloadTable();
            loadWidgets();
          }}
        />
      </Modal>

      <Modal
        show={settingsModal}
        onHide={() => setSettingsModal(false)}
        size="lg"
      >
        <SettingsContact
          data={selectedContact}
          onClose={() => setSettingsModal(false)}
          onHandleReload={() => {
            reloadTable();
            loadWidgets();
            setSettingsModal(false);
          }}
          type="entity"
        />
      </Modal>

      <BulkNewsletterModal
        show={newsletterModal}
        onHide={() => {
          setNewsletterModal(false);
          setNewsletter("yes");
        }}
        newsletter={newsletter}
        setNewsletter={setNewsletter}
        onSubmit={handleNewsletterUpdate}
        isLoading={isLoading}
        isVisible={checkedRows.length > 0}
      />

      <BulkManagingContactModal
        show={managingContactModal}
        onHide={() => {
          setManagingContactModal(false);
          setManagingContact(null);
        }}
        managingContact={managingContact}
        setManagingContact={setManagingContact}
        managingContactList={managingContactList}
        onSubmit={handleManagingContactUpdate}
        isLoading={isLoading}
        isVisible={checkedRows.length > 0}
      />

      <AdditionalFiltersModal
        show={additionalFiltersModal}
        onHide={() => setAdditionalFiltersModal(false)}
        onApply={handleApplyAdditionalFilter}
        onReset={handleResetAdditionalFilter}
        additionalFilter={additionalFilter}
        handleUpdateFilter={handleUpdateFilter}
        teamMembers={teamMembers}
        owner={owner}
        category={category}
        constants={constants}
        categoryList={categoryList}
      />

      <Modal
        show={secondaryAccountModal}
        onHide={() => setSecondaryAccountModal(false)}
        backdrop="static"
        centered
      >
        <NewTrader
          entity_id={entity_id}
          role={constants.BUSINESS_TYPE.PURCHASE}
          title="Secondary Account"
          productDetails={{}}
          source="accounts"
          onCloseModal={() => {
            setSecondaryAccountModal(false);
            reloadTable();
            loadWidgets();
          }}
        />
      </Modal>
    </>
  );
}

export default AllEntityTeamMembers;